import React, { useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import type { IncomingCallState } from '../types/call.types';

interface IncomingCallModalProps {
  visible: boolean;
  incomingCall: IncomingCallState | null;
  isAccepting: boolean;
  onAccept: () => void;
  onReject: () => void;
}

export const IncomingCallModal: React.FC<IncomingCallModalProps> = ({
  visible, incomingCall, isAccepting, onAccept, onReject,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio();
    audio.loop = true;
    audio.volume = 0.6;
    // Short repeating beep — replaced with real ringtone if desired
    // To use a file: import Ringtone from '../../../assets/ringtone.mp3'
    // then: audio.src = Ringtone;
    audioRef.current = audio;
    return () => { audio.pause(); };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (visible && !isAccepting) {
      audio.play().catch(() => { /* autoplay blocked — no-op */ });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [visible, isAccepting]);

  const callerName = incomingCall?.callerName?.trim() || 'AR Device';

  const footer = (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
    }}>
      <button
        className="btn btn--primary"
        onClick={onAccept}
        disabled={isAccepting}
        style={{
          minWidth: '130px',
          background: '#16a34a',
          borderColor: '#16a34a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.4rem',
        }}
      >
        {isAccepting
          ? <i className="pi pi-spin pi-spinner"
            style={{ fontSize: '0.9rem' }} />
          : <i className="pi pi-phone"
            style={{ fontSize: '0.9rem' }} />
        }
        {isAccepting ? 'Connecting…' : 'Accept'}
      </button>

      <button
        className="btn"
        onClick={onReject}
        disabled={isAccepting}
        style={{
          minWidth: '130px',
          background: '#dc2626',
          borderColor: '#dc2626',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.4rem',
        }}
      >
        <i className="pi pi-phone"
          style={{
            fontSize: '0.9rem',
            transform: 'rotate(135deg)',
            display: 'block',
          }} />
        Reject
      </button>
    </div>
  );

  return (
    <Dialog
      header="Incoming Call"
      visible={visible}
      onHide={() => { /* controlled by hook — never self-close */ }}
      closable={false}
      footer={footer}
      modal
      draggable={false}
      resizable={false}
      style={{ width: '360px' }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.75rem 0',
        textAlign: 'center',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: '#dcfce7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: isAccepting
            ? 'none'
            : 'pulse 1.4s ease-in-out infinite',
        }}>
          <i className="pi pi-phone"
            style={{ fontSize: '1.8rem', color: '#16a34a' }} />
        </div>

        <div style={{
          fontWeight: 600, fontSize: '1rem', color: '#111827',
        }}>
          {callerName} is calling you
        </div>

        <div style={{
          fontSize: '0.8rem', color: '#6b7280',
        }}>
          {isAccepting ? 'Setting up video call…' : 'Incoming video call'}
        </div>
      </div>
    </Dialog>
  );
};
